import type { TransactionDetailDto } from './transactionDetailTypes';

/**
 * On-demand receipt generator.
 * Produces a plain-text receipt from a canonical TransactionDetailDto.
 * The receipt is generated entirely client-side — no Azure storage required.
 */

const fmt = (n: number | null | undefined, dec = 2) =>
  n == null ? 'N/A' : `$${Math.abs(n).toFixed(dec)}`;

const fmtDate = (iso?: string | null) => {
  if (!iso) return 'N/A';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const hr = (char = '─', len = 50) => char.repeat(len);

export interface GeneratedReceipt {
  text: string;
  filename: string;
}

export function generateTransactionReceipt(detail: TransactionDetailDto): GeneratedReceipt {
  const { summary, identity, timing, classification, parties, amounts, fees, context } = detail;
  const rawType = (summary.typeRaw || classification.subType || '').toUpperCase();
  const canonicalType = (classification.canonicalType || '').toUpperCase();
  const isPaymentFastPayout =
    rawType === 'PAYOUT_SEND' ||
    rawType === 'PAYOUT_RECEIVE' ||
    canonicalType === 'PAYOUT' ||
    !!context.payout?.paymentId;
  const isVendorCreditReceipt =
    canonicalType === 'PROMO' ||
    !!context.promo?.promoCreditId ||
    !!identity.linked?.promoHistoryId ||
    Math.abs(amounts.vendorCreditsUsed || 0) > 0;
  const postageLineLabel = isVendorCreditReceipt
    ? 'Vendor Credit:'
    : (isPaymentFastPayout ? 'Postage Credit:' : 'Postage:');
  const payoutPostageCredit = isPaymentFastPayout
    ? (fees.components.deliveryFee || amounts.attachedAmount || amounts.grossAmount || 0)
    : fees.components.deliveryFee;
  const payoutPostageFees = isPaymentFastPayout
    ? ((fees.postageFeesTotal ?? (fees.components.deliveryFee + fees.components.mailServerFee)) +
       (fees.attachedCreditsFeesTotal ?? fees.components.clearingHouseFee))
    : (fees.postageFeesTotal ?? (fees.components.deliveryFee + fees.components.mailServerFee));
  const isPurchaseType = rawType === 'PURCHASE' || rawType === 'PRODUCT_PURCHASE' || rawType === 'CREDIT_PURCHASE';

  const isSellerReceipt = rawType === 'CREDIT_PRODUCT_SALE';
  const isAffiliateReceipt = rawType === 'CREDIT_AFFILIATE_COMMISSION';
  const isReferrerReceipt = rawType === 'CREDIT_FORWARDER_COMMISSION';
  const shouldShowSplitSection = isSellerReceipt || isAffiliateReceipt || isReferrerReceipt;
  // Commission amounts are derived from a percentage of the base; show 4 dp so the
  // underlying calculation (e.g. 0.1250 × rate) is unambiguous to the recipient.
  const isPercentageBased = isSellerReceipt || isAffiliateReceipt || isReferrerReceipt;

  const actor = parties.actor;
  const counterparty = parties.counterparty;
  const isSender = actor.role === 'SENDER' || actor.role === 'BUYER';

  const lines: string[] = [];

  const add = (s = '') => lines.push(s);

  add(hr('═'));
  add('          QIX CREDITS TRANSACTION RECEIPT');
  add(hr('═'));
  add();
  add(`Type:       ${summary.typeDisplay || classification.displayType || classification.canonicalType}`);
  add(`Status:     ${summary.statusComputed || classification.status || 'completed'}`);
  add(`Date:       ${fmtDate(summary.initiatedAtUtc || timing.initiatedAtUtc)}`);
  add(`Completed:  ${summary.completedAtUtc || timing.completedAtUtc ? fmtDate(summary.completedAtUtc || timing.completedAtUtc) : 'Pending'}`);
  add();

  if (actor.username) {
    const fromLabel = isSender ? 'From:' : 'To:';
    const toLabel = isSender ? 'To:' : 'From:';
    add(`${fromLabel.padEnd(12)}${actor.aliasUsed && actor.aliasUsed !== actor.username
      ? `${actor.username} (via ${actor.aliasUsed})`
      : actor.username}`);
    if (counterparty?.username) {
      add(`${toLabel.padEnd(12)}${counterparty.username}`);
    }
    add();
  }

  if (summary.subject || context.subject) {
    add(`Subject:    ${summary.subject || context.subject}`);
    add();
  }

  add(hr());
  add('AMOUNTS');
  add(hr());
  add(`${postageLineLabel} ${fmt(payoutPostageCredit, 4)}`);
  if (!isPaymentFastPayout) {
    // Use 4 dp for percentage-based commissions so the base calculation amount
    // (e.g. $0.1250) is shown precisely rather than rounded to $0.13.
    add(`Attached Credits: ${fmt(amounts.attachedAmount, isPercentageBased ? 4 : 2)}`);
  }
  if (isPurchaseType) {
    add(`Purchase Price: ${fmt(amounts.grossAmount)}`);
  }
  add(`Net Delta:     ${amounts.amountDelta >= 0 ? '+' : ''}${amounts.amountDelta.toFixed(4)}`);
  add(`Balance Before: ${fmt(amounts.balanceBefore)}`);
  add(`Balance After:  ${fmt(amounts.balanceAfter)}`);
  add();

  if (shouldShowSplitSection) {
    add(hr());
    add('REVENUE SPLIT');
    add(hr());
    if (isSellerReceipt) {
      if (amounts.splits.vendorAmount != null) add(`  Vendor Net:     ${fmt(amounts.splits.vendorAmount, 4)}`);
      if (amounts.splits.affiliateAmount != null) add(`  Affiliate:      ${fmt(amounts.splits.affiliateAmount, 4)}`);
      if (amounts.splits.forwarderAmount != null) add(`  Referrer:       ${fmt(amounts.splits.forwarderAmount, 4)}`);
    } else if (isAffiliateReceipt) {
      if (amounts.splits.affiliateAmount != null) add(`  Affiliate:      ${fmt(amounts.splits.affiliateAmount, 4)}`);
    } else if (isReferrerReceipt) {
      if (amounts.splits.forwarderAmount != null) add(`  Referrer:       ${fmt(amounts.splits.forwarderAmount, 4)}`);
    }
    add();
  }

  add(hr());
  add('FEES');
  add(hr());
  add(`  Postage Fees:        ${fmt(payoutPostageFees, 4)}`);
  if (!isPaymentFastPayout) {
    add(`  Attached Credits:    ${fmt(fees.attachedCreditsFeesTotal ?? fees.components.clearingHouseFee, 4)}`);
  }
  if (fees.feeScheduleName || fees.feeVersionCode) {
    const schedule = fees.feeScheduleName || fees.feeVersionCode;
    add(`  Fee Schedule:        ${schedule}${fees.resolutionFallback ? ' (fallback)' : ''}`);
  }
  add();
  if (context.product?.productName) {
    add(`Product:    ${context.product.productName}`);
  }
  if (context.sourceUrl) {
    add(`Source:     ${context.sourceUrl}`);
  }
  add();

  add(hr('─', 30));
  add(`Transaction ID: ${identity.transactionId}`);
  add(`Generated:      ${new Date().toLocaleString()}`);
  add(hr('═'));

  const dateTag = timing.initiatedAtUtc.substring(0, 10).replace(/-/g, '');
  const filename = `qix-receipt-${dateTag}-${identity.transactionId.substring(0, 8)}.txt`;

  return { text: lines.join('\n'), filename };
}
